'use client';

import React, { useState } from 'react';
import { MapPin, Train, ShoppingBag, GraduationCap, Hospital, Bus, Utensils, TreePine, Waves } from 'lucide-react';

const NEARBY_PLACES = [
  { id: 'nb-metro', icon: <Train size={14} />, label: 'Kadıköy Metro', distance: '650 m', time: '8 dk yürüyüş', type: 'transport', color: 'text-primary bg-primary-light' },
  { id: 'nb-okul', icon: <GraduationCap size={14} />, label: 'Kadıköy Anadolu Lisesi', distance: '420 m', time: '5 dk yürüyüş', type: 'education', color: 'text-success bg-success-light' },
  { id: 'nb-hastane', icon: <Hospital size={14} />, label: 'Kadıköy Devlet Hastanesi', distance: '1.2 km', time: '15 dk yürüyüş', type: 'health', color: 'text-danger bg-danger-light' },
  { id: 'nb-market', icon: <ShoppingBag size={14} />, label: 'Carrefour SA', distance: '280 m', time: '3 dk yürüyüş', type: 'shopping', color: 'text-accent bg-orange-50' },
  { id: 'nb-otobus', icon: <Bus size={14} />, label: 'Moda Durağı', distance: '180 m', time: '2 dk yürüyüş', type: 'transport', color: 'text-primary bg-primary-light' },
  { id: 'nb-restoran', icon: <Utensils size={14} />, label: 'Moda Çarşısı', distance: '350 m', time: '4 dk yürüyüş', type: 'food', color: 'text-warning bg-warning-light' },
  { id: 'nb-park', icon: <TreePine size={14} />, label: 'Moda Parkı', distance: '200 m', time: '3 dk yürüyüş', type: 'park', color: 'text-success bg-success-light' },
  { id: 'nb-sahil', icon: <Waves size={14} />, label: 'Moda Sahili', distance: '500 m', time: '6 dk yürüyüş', type: 'leisure', color: 'text-blue-500 bg-blue-50' },
];

const TRANSPORT_TIMES = [
  { id: 'tt-taksim', destination: 'Taksim Meydanı', ferry: '25 dk', metro: '35 dk', car: '40 dk' },
  { id: 'tt-besiktas', destination: 'Beşiktaş', ferry: '20 dk', metro: '32 dk', car: '35 dk' },
  { id: 'tt-ataturk', destination: 'Atatürk Havalimanı', ferry: null, metro: '55 dk', car: '45 dk' },
  { id: 'tt-sabiha', destination: 'Sabiha Gökçen', ferry: null, metro: null, car: '40 dk' },
];

export default function PropertyMap() {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const filteredPlaces = activeFilter
    ? NEARBY_PLACES?.filter(p => p?.type === activeFilter)
    : NEARBY_PLACES;

  return (
    <div className="space-y-6">
      {/* Map Placeholder */}
      {/* Backend integration point: Mapbox/Google Maps embed with property location */}
      <div className="relative rounded-2xl overflow-hidden h-72 map-placeholder">
        <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="detailMapGrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#6366f1" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#detailMapGrid)" />
        </svg>
        <svg className="absolute inset-0 w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
          <line x1="0" y1="35%" x2="100%" y2="40%" stroke="white" strokeWidth="3" />
          <line x1="0" y1="65%" x2="100%" y2="62%" stroke="white" strokeWidth="2" />
          <line x1="25%" y1="0" x2="28%" y2="100%" stroke="white" strokeWidth="3" />
          <line x1="55%" y1="0" x2="58%" y2="100%" stroke="white" strokeWidth="2" />
          <line x1="80%" y1="0" x2="82%" y2="100%" stroke="white" strokeWidth="1.5" />
        </svg>

        {/* Property Pin */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-primary border-4 border-white shadow-xl flex items-center justify-center">
              <MapPin size={18} className="text-white" />
            </div>
            <div className="mt-1 px-3 py-1 bg-white rounded-lg shadow-lg text-xs font-bold text-foreground whitespace-nowrap">
              Moda Cad. No:42
            </div>
          </div>
        </div>

        {/* Nearby icons on map */}
        <div className="absolute" style={{ top: '28%', left: '38%' }}>
          <div className="w-7 h-7 rounded-full bg-primary-light border border-primary flex items-center justify-center shadow-sm">
            <Train size={12} className="text-primary" />
          </div>
        </div>
        <div className="absolute" style={{ top: '60%', left: '62%' }}>
          <div className="w-7 h-7 rounded-full bg-success-light border border-success flex items-center justify-center shadow-sm">
            <GraduationCap size={12} className="text-success" />
          </div>
        </div>
        <div className="absolute" style={{ top: '42%', left: '72%' }}>
          <div className="w-7 h-7 rounded-full bg-orange-50 border border-accent flex items-center justify-center shadow-sm">
            <ShoppingBag size={12} className="text-accent" />
          </div>
        </div>
        <div className="absolute" style={{ top: '70%', left: '30%' }}>
          <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-400 flex items-center justify-center shadow-sm">
            <Waves size={12} className="text-blue-500" />
          </div>
        </div>

        <div className="absolute bottom-3 right-3">
          <span className="text-xs text-foreground/40 font-medium bg-white/70 px-2 py-1 rounded-lg">© Evora Harita · Tahmini konum</span>
        </div>
      </div>

      {/* Filter Chips */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
        <button
          onClick={() => setActiveFilter(null)}
          className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${activeFilter === null ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:border-primary hover:text-primary'}`}
        >
          Tümü
        </button>
        {[
          { value: 'transport', label: 'Ulaşım' },
          { value: 'education', label: 'Eğitim' },
          { value: 'health', label: 'Sağlık' },
          { value: 'shopping', label: 'Alışveriş' },
          { value: 'food', label: 'Yeme-İçme' },
          { value: 'park', label: 'Park' },
          { value: 'leisure', label: 'Eğlence' },
        ]?.map((f) => (
          <button
            key={`nb-filter-${f?.value}`}
            onClick={() => setActiveFilter(activeFilter === f?.value ? null : f?.value)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${activeFilter === f?.value ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:border-primary hover:text-primary'}`}
          >
            {f?.label}
          </button>
        ))}
      </div>

      {/* Nearby Places */}
      <div>
        <h3 className="text-base font-bold text-foreground mb-3">Yakın Çevre</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {filteredPlaces?.map((place) => (
            <div key={place?.id} className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted transition-colors">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${place?.color}`}>
                {place?.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground line-clamp-1">{place?.label}</p>
                <p className="text-xs text-muted-foreground">{place?.time}</p>
              </div>
              <span className="text-xs font-bold text-muted-foreground flex-shrink-0">{place?.distance}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Transport Times */}
      <div>
        <h3 className="text-base font-bold text-foreground mb-3">Ulaşım Süreleri</h3>
        <div className="card-elevated rounded-2xl overflow-hidden">
          <div className="grid grid-cols-4 px-4 py-2.5 bg-muted border-b border-border text-xs font-bold text-muted-foreground uppercase tracking-wide">
            <span className="col-span-1">Hedef</span>
            <span className="text-center">Vapur</span>
            <span className="text-center">Metro</span>
            <span className="text-center">Araba</span>
          </div>
          {TRANSPORT_TIMES?.map((tt) => (
            <div key={tt?.id} className="grid grid-cols-4 px-4 py-3 border-b border-border last:border-0 hover:bg-muted/50 transition-colors items-center">
              <span className="text-sm font-medium text-foreground col-span-1">{tt?.destination}</span>
              <span className="text-sm text-center tabular-nums">{tt?.ferry ?? <span className="text-muted-foreground">—</span>}</span>
              <span className="text-sm text-center tabular-nums">{tt?.metro ?? <span className="text-muted-foreground">—</span>}</span>
              <span className="text-sm text-center tabular-nums">{tt?.car}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Süreler tahmini olup trafik koşullarına göre değişebilir.</p>
      </div>
    </div>
  );
}